import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getFinancialReport } from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import { FiDollarSign } from 'react-icons/fi';

const FinancialSummary = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ totalIncome: 0, totalExpense: 0, balance: 0, byFund: [], monthly: [] });
  const [from, setFrom] = useState(new Date(new Date().getFullYear(), 0, 1).toISOString().slice(0, 10));
  const [to, setTo] = useState(new Date().toISOString().slice(0, 10));

  useEffect(() => {
    getFinancialReport({ from, to }).then(r => setData(r.data || { totalIncome: 0, totalExpense: 0, balance: 0, byFund: [], monthly: [] })).catch(() => {});
  }, [from, to]);

  const chartData = [...data.monthly].reverse();

  return (
    <div className="page-content">
      <div className="page-header">
        <div><h1>{t('nav.financialSummary')}</h1></div>
      </div>

      <div className="filters-row">
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.from')}</label>
        <input className="filter-input" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        <label style={{ fontSize: 13, color: '#6b7280', fontWeight: 500 }}>{t('table.to')}</label>
        <input className="filter-input" type="date" value={to} onChange={e => setTo(e.target.value)} />
      </div>

      <div className="stat-cards" style={{ gridTemplateColumns: 'repeat(3,1fr)', marginBottom: 20 }}>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dcfce7', color: '#16a34a' }}><FiDollarSign /></div>
          <div><div className="stat-label">{t('dashboard.income')}</div><div className="stat-value" style={{ color: '#16a34a' }}>{parseFloat(data.totalIncome).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2', color: '#dc2626' }}><FiDollarSign /></div>
          <div><div className="stat-label">{t('dashboard.expense')}</div><div className="stat-value" style={{ color: '#dc2626' }}>{parseFloat(data.totalExpense).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#dbeafe', color: '#2563eb' }}><FiDollarSign /></div>
          <div><div className="stat-label">{t('dashboard.balance')}</div><div className="stat-value" style={{ color: parseFloat(data.balance) >= 0 ? '#2563eb' : '#dc2626' }}>{parseFloat(data.balance).toLocaleString(undefined, { minimumFractionDigits: 2 })}</div></div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 20, marginBottom: 20 }}>
        <div className="card">
          <div className="card-header">{t('dashboard.income')}</div>
          <div className="card-body">
            {chartData.length === 0 ? (
              <div className="empty-state"><p>{t('finance.noRecords')}</p></div>
            ) : (
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v) => [v.toLocaleString(undefined, { minimumFractionDigits: 2 }), 'Income']} />
                  <Bar dataKey="income" fill="#4f46e5" radius={[4, 4, 0, 0]} name="Income" />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>

        <div className="card">
          <div className="card-header">Income by Fund</div>
          <div className="card-body" style={{ padding: 0 }}>
            {data.byFund.length === 0 ? (
              <div className="empty-state"><p>No fund data</p></div>
            ) : data.byFund.map((f, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: i < data.byFund.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <span style={{ fontSize: 13, color: '#374151' }}>{f.name || 'General'}</span>
                <span style={{ fontWeight: 700, color: '#4f46e5' }}>{parseFloat(f.total).toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FinancialSummary;
